import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Loader, BarChart, Briefcase, Star, X } from 'lucide-react';
import api from '../../services/api';
import { debounce } from 'lodash';
import ChartModal from './ChartModal';
import TradeModal from './TradeModal';
import CompanyProfileModal from './CompanyProfileModal';

function StockCardWithLivePrice({ stock, setChartSymbol, setTradeSymbol, setCompanyProfileSymbol, watchlist, toggleWatchlist, watchlistActionLoading }) {
  const [staticInfo, setStaticInfo] = useState(null); // name, symbol, etc.
  const [liveInfo, setLiveInfo] = useState({ price: null, today_change: null, today_change_percent: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const baseSymbol = stock.symbol.endsWith('.L') ? stock.symbol.replace('.L', '') : stock.symbol;
    // Initial fetch: get all info
    setLoading(true);
    setError('');
    
    // Initial fetch: get all info
    setLoading(true);
    setError('');
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/market-data/company-profile/?symbol=${baseSymbol}`)
      .then(res => res.json())
      .then(data => {
        if (isMounted) {
          setStaticInfo({
            symbol: stock.symbol,
            name: data.name,
            exchange: stock.exchange,
          });
          setLiveInfo({
            price: data.price,
            today_change: data.today_change,
            today_change_percent: data.today_change_percent,
          });
        }
      })
      .catch(() => {
        if (isMounted) setError('Failed to fetch price.');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    // Poll only price and today's change
    const fetchLive = () => {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/market-data/company-profile/?symbol=${baseSymbol}`)
        .then(res => res.json())
        .then(data => {
          if (isMounted) {
            setLiveInfo({
              price: data.price,
              today_change: data.today_change,
              today_change_percent: data.today_change_percent,
            });
          }
        });
    };
    
    const interval = setInterval(fetchLive, 25000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [stock.symbol]);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    return typeof price === 'number' ? price.toFixed(2) : '0.00';
  };
  const formatPriceChange = (change) => {
    if (change === null || change === undefined) return '0.00';
    return typeof change === 'number' ? change.toFixed(2) : '0.00';
  };
  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined) return '0.00';
    return typeof percent === 'number' ? percent.toFixed(2) : '0.00';
  };

  const isWatchlisted = watchlist && watchlist.includes(stock.symbol);

  // Separate component for live price display to prevent full card re-render
  const LivePriceDisplay = React.memo(({ liveInfo, loading, error }) => {
    return (
      <div className="mt-1">
        <span className="font-semibold">Current Price: </span>
        {loading ? 'Loading...' : error ? <span className="text-red-600">{error}</span> : liveInfo && liveInfo.price !== undefined ? (
          <span className={liveInfo.today_change > 0 ? 'text-green-600' : liveInfo.today_change < 0 ? 'text-red-600' : ''}>
            £{formatPrice(liveInfo.price)}
          </span>
        ) : 'N/A'}
      </div>
    );
  });

  // Separate component for live change display
  const LiveChangeDisplay = React.memo(({ liveInfo, loading, error }) => {
    return (
      <div>
        <span className="font-semibold">Today's Change: </span>
        {loading ? 'Loading...' : error ? <span className="text-red-600">{error}</span> : liveInfo && liveInfo.today_change !== undefined ? (
          <span className={liveInfo.today_change > 0 ? 'text-green-600' : liveInfo.today_change < 0 ? 'text-red-600' : ''}>
            {liveInfo.today_change > 0 ? '+' : ''}{formatPriceChange(liveInfo.today_change)}
            {liveInfo.today_change_percent !== undefined ? ` (${liveInfo.today_change_percent > 0 ? '+' : ''}${formatPercentage(liveInfo.today_change_percent)}%)` : ''}
          </span>
        ) : 'N/A'}
      </div>
    );
  });

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">{staticInfo?.symbol || stock.symbol}</h3>
            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-semibold rounded-full">
              {staticInfo?.exchange || stock.exchange || 'LSE'}
            </span>
          </div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">{staticInfo?.name || ''}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <LivePriceDisplay liveInfo={liveInfo} loading={loading} error={error} />
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <LiveChangeDisplay liveInfo={liveInfo} loading={loading} error={error} />
            </div>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-3">
          <button
            className="relative p-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
            title={isWatchlisted ? 'Remove from Watchlist' : 'Add to Watchlist'}
            onClick={() => toggleWatchlist(stock.symbol)}
            disabled={watchlistActionLoading}
          >
            {watchlistActionLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 text-yellow-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              </span>
            ) : (
              <Star
                className={`h-6 w-6 ${isWatchlisted ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 hover:text-yellow-400'} transition-all duration-200`}
                fill={isWatchlisted ? '#facc15' : 'none'}
                strokeWidth={2}
              />
            )}
          </button>
        </div>
      </div>
      
      <div className="flex flex-nowrap gap-1.5">
        <button
          className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-xs font-semibold shadow-md hover:shadow-lg flex-shrink-0"
          onClick={() => setChartSymbol(stock.symbol)}
        >
          <BarChart className="h-3.5 w-3.5" />
          <span>Chart</span>
        </button>
        <button
          className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-md hover:from-green-600 hover:to-green-700 transition-all duration-200 text-xs font-semibold shadow-md hover:shadow-lg flex-shrink-0"
          onClick={() => setTradeSymbol(stock.symbol)}
        >
          <Briefcase className="h-3.5 w-3.5" />
          <span>Trade</span>
        </button>
        <button
          className="flex items-center space-x-1 px-2.5 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-md hover:from-purple-600 hover:to-purple-700 transition-all duration-200 text-xs font-semibold shadow-md hover:shadow-lg flex-shrink-0"
          onClick={() => setCompanyProfileSymbol(stock.symbol)}
        >
          <span className="text-sm">🏢</span>
          <span>Profile</span>
        </button>
      </div>
    </div>
  );
}

const StockSearch = ({ onTradeComplete, watchlist = [], toggleWatchlist = () => {}, showWatchlistOnly = false }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [chartSymbol, setChartSymbol] = useState(null);
    const [tradeSymbol, setTradeSymbol] = useState(null);
    const [companyProfileSymbol, setCompanyProfileSymbol] = useState(null);
    const [watchlistActionLoading, setWatchlistActionLoading] = useState(false);

    const fetchResults = async (searchQuery) => {
        if (showWatchlistOnly) {
            return; // handled by watchlist effect
        }
        if (!searchQuery) {
            setResults([]);
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await api.get(`/trading/stocks/search/?q=${searchQuery}`);
            if (response.data.success) {
                setResults(response.data.data);
            } else {
                setError('Failed to fetch results.');
            }
        } catch (err) {
            console.error('Search error:', err);
            setError('Failed to fetch results.');
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetch = useCallback(debounce(fetchResults, 800), []);

    useEffect(() => {
        if (showWatchlistOnly) return;
        debouncedFetch(query);
    }, [query, debouncedFetch, showWatchlistOnly]);

    // Polling: auto-refresh search results every 25 seconds
    useEffect(() => {
        if (showWatchlistOnly) return;
        if (!query) return;
        const interval = setInterval(() => {
            fetchResults(query);
        }, 25000);
        return () => clearInterval(interval);
    }, [query, showWatchlistOnly]);

    // When showing watchlist, populate results directly from watchlist symbols
    useEffect(() => {
        if (!showWatchlistOnly) return;
        const uniqueSymbols = Array.from(new Set(watchlist || []));
        const items = uniqueSymbols.map(symbol => ({ symbol, exchange: 'LSE' }));
        setResults(items);
    }, [watchlist, showWatchlistOnly]);

    const filteredResults = showWatchlistOnly
      ? results.filter(stock => watchlist.includes(stock.symbol))
      : results;

    return (
        <div className="space-y-6">
            <ChartModal symbol={chartSymbol} onClose={() => setChartSymbol(null)} />
            <TradeModal
                symbol={tradeSymbol}
                onClose={() => setTradeSymbol(null)}
                onTradeComplete={onTradeComplete}
            />
            <CompanyProfileModal symbol={companyProfileSymbol} onClose={() => setCompanyProfileSymbol(null)} />
            
            {/* Premium Search Bar */}
            <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                    <Search className="h-6 w-6 text-gray-400" />
                </div>
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={showWatchlistOnly ? "Search within your watchlist..." : "Search for UK companies (e.g., Barclays, BARC.L, Tesco, TSCO.L)..."}
                    className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 shadow-lg hover:shadow-xl"
                    disabled={showWatchlistOnly}
                />
                {query && (
                    <button
                        onClick={() => setQuery('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                )}
            </div>

            {loading && (
                <div className="flex justify-center items-center py-8">
                    <Loader className="animate-spin h-8 w-8 text-primary-500" />
                </div>
            )}
            
            {error && <p className="text-red-500">{error}</p>}

            {!loading && filteredResults.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredResults.map(stock => (
                        <StockCardWithLivePrice
                          key={stock.symbol}
                          stock={stock}
                          setChartSymbol={setChartSymbol}
                          setTradeSymbol={setTradeSymbol}
                          setCompanyProfileSymbol={setCompanyProfileSymbol}
                          watchlist={watchlist}
                          toggleWatchlist={toggleWatchlist}
                          watchlistActionLoading={watchlistActionLoading}
                        />
                    ))}
                </div>
            )}
            
            {!loading && query && filteredResults.length === 0 && !showWatchlistOnly && (
                <div className="text-center py-12">
                    <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Search className="h-8 w-8 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No results found</h4>
                    <p className="text-gray-600 dark:text-gray-300 mb-2">No companies found for "{query}"</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Try searching with a different company name or stock symbol.</p>
                </div>
            )}
            
            {!loading && showWatchlistOnly && filteredResults.length === 0 && (
                <div className="text-center py-12">
                    <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Star className="h-8 w-8 text-gray-400" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Watchlist is empty</h4>
                    <p className="text-gray-600 dark:text-gray-300 mb-2">No stocks in your watchlist yet</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Star stocks from the search results to add them to your watchlist.</p>
                </div>
            )}
            
            {!loading && !query && !showWatchlistOnly && (
                <div className="text-center py-12">
                    <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                        <Search className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Search UK Stocks</h4>
                    <p className="text-gray-600 dark:text-gray-300 mb-2">Discover and analyze UK companies</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Enter a company name or symbol to get started.</p>
                </div>
            )}
        </div>
    );
};

export default StockSearch; 